import { useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PhoneLink } from '@/components/PhoneLink'

type Supplier = {
  id: string
  name: string
  type: string | null
  tier: string | null
  city: string | null
  province: string | null
  parts_phone: string | null
  hours_parts: string | null
  api_available: boolean
  api_provider: string | null
  api_status: string | null
  account_number: string | null
  notes: string | null
  calls_to_date: number
  successful_calls: number
}

function successRate(s: Supplier) {
  if (s.calls_to_date <= 0) return null
  return Math.round((s.successful_calls / s.calls_to_date) * 100)
}

export function Suppliers() {
  const { t } = useTranslation('suppliers')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const [search, setSearch] = useState('')

  const suppliersQuery = useQuery({
    queryKey: ['suppliers'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch('/api/suppliers', {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }

      return (await res.json()) as Supplier[]
    },
  })

  const filtered = useMemo(() => {
    const data = suppliersQuery.data ?? []
    const q = search.trim().toLowerCase()
    if (!q) return data
    return data.filter((s) => {
      return (
        s.name.toLowerCase().includes(q) ||
        (s.type ?? '').toLowerCase().includes(q) ||
        (s.city ?? '').toLowerCase().includes(q)
      )
    })
  }, [suppliersQuery.data, search])

  const apiCount = (suppliersQuery.data ?? []).filter((s) => s.api_available).length

  return (
    <div className="space-y-4">
      {/* §4.5 page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('header.subtitle', {
              count: suppliersQuery.data?.length ?? 0,
              api_count: apiCount,
            })}
          </p>
        </div>

        {/* §4.10 search input: 48px, 14px radius, leading ink-3 icon */}
        <div className="relative w-full sm:w-72">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
            placeholder={t('search.placeholder')}
            className="h-12 rounded-[14px] pl-9"
          />
        </div>
      </div>

      {suppliersQuery.isError ? (
        <div className="rounded-[10px] border border-destructive/30 bg-destructive-soft p-4 text-sm text-destructive">
          {suppliersQuery.error instanceof Error
            ? suppliersQuery.error.message
            : t('error')}
        </div>
      ) : null}

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        {filtered.map((s) => {
          const pct = successRate(s)
          return (
            <Card key={s.id} className="rounded-[14px] ring-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[15px] font-[620] text-foreground">{s.name}</span>
                    {/* §4.9 neutral chips — tier and API availability are facts. */}
                    {s.tier === 'primary' ? (
                      <span className="rounded-full border border-border bg-muted px-[9px] py-[3px] text-[12px] font-[650] text-muted-foreground">
                        {t('card.primary_badge')}
                      </span>
                    ) : null}
                    {s.api_available ? (
                      <span className="rounded-full border border-border bg-muted px-[9px] py-[3px] text-[12px] font-[650] text-muted-foreground">
                        {t('card.api_badge')}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {s.type ?? '—'}
                    {s.city ? ` · ${s.city}` : ''}
                  </div>
                </div>

                <div className="text-right text-xs">
                  <div className="text-ink-3">{t('card.success_rate')}</div>
                  <div className="font-mono font-semibold text-foreground">
                    {pct === null ? '—' : `${pct}%`}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[12px] font-medium text-ink-3">
                    {t('card.parts_line')}
                  </div>
                  <div className="font-mono text-foreground">
                    <PhoneLink phone={s.parts_phone} />
                  </div>
                </div>
                <div>
                  <div className="text-[12px] font-medium text-ink-3">
                    {t('card.hours')}
                  </div>
                  <div className="text-muted-foreground">{s.hours_parts || '—'}</div>
                </div>
                {s.account_number ? (
                  <div>
                    <div className="text-[12px] font-medium text-ink-3">
                      {t('card.account')}
                    </div>
                    <div className="font-mono text-muted-foreground">{s.account_number}</div>
                  </div>
                ) : null}
                {s.api_provider ? (
                  <div>
                    <div className="text-[12px] font-medium text-ink-3">
                      {t('card.api_label')}
                    </div>
                    <div className="text-muted-foreground">
                      {s.api_provider}
                      {s.api_status ? ` (${s.api_status.replace(/_/g, ' ')})` : ''}
                    </div>
                  </div>
                ) : null}
              </div>

              {s.notes ? (
                <div className="mt-3 rounded-[10px] border border-border bg-muted/40 p-2 text-xs italic text-muted-foreground">
                  {s.notes}
                </div>
              ) : null}
            </Card>
          )
        })}

        {filtered.length === 0 && !suppliersQuery.isLoading ? (
          <div className="col-span-full rounded-[14px] border border-dashed border-border bg-muted/30 p-12 text-center text-sm text-muted-foreground">
            {t('empty')}
          </div>
        ) : null}
      </div>
    </div>
  )
}
